// src/helpers/tickers/CrawlStaggeredTicker.ts
import type { AnimationI, TickerI } from "tweenly"
import gsap from "gsap"
import GSDevTools from "gsap/GSDevTools"
import { applyJoinedCrawlTexts } from "../subtimeline.helpers"
import { BaseTicker } from "./BaseTicker"
import { CrawlStaggeredCtx } from "./types"

gsap.registerPlugin(GSDevTools)

export class CrawlStaggeredTicker extends BaseTicker {
    private readonly maskObject: TickerI
    private readonly animationGroup: AnimationI[]
    private readonly textListNode: HTMLElement | null

    private readonly maskWidth: number
    private readonly duration: number

    private lastWidth: number
    private newWidths: Record<string, number>
    private textsWidth: number
    private crawlSpeed: number

    constructor(args: CrawlStaggeredCtx) {
        super(args.timeline, args.objectId, args.joinedTexts, args.childTexts, args.assets)

        this.maskObject = args.maskObject
        this.animationGroup = args.animationGroup
        this.textListNode = args.textListNode

        this.maskWidth = args.maskWidth
        this.duration = args.duration

        this.lastWidth = args.lastWidth
        this.newWidths = args.newWidths
        this.textsWidth = args.textsWidth
        this.crawlSpeed = Number((this.maskWidth + this.textsWidth) / this.duration)
    }

    protected intro() {
        const { timeline, maskObject, textsWidth, crawlSpeed } = this

        const tickerObjectElement =
            maskObject.parentId && (document.getElementById(maskObject.parentId) as HTMLElement)
        if (tickerObjectElement) {
            timeline.fromTo(
                `#${maskObject.id}`,
                { transform: `translateX(${tickerObjectElement.clientWidth}px)` },
                {
                    transform: `translateX(-${textsWidth}px)`,
                    ease: "none",
                    duration: crawlSpeed,
                },
                0
            )
        }
        this.lastWidth = 0
        for (const childId of maskObject.childIds) {
            if (this.lastWidth === 0) {
                timeline.set(
                    `#${childId}`,
                    {
                        transform: `translateX(${0}px)`,
                    },
                    0
                )
                this.lastWidth = this.newWidths[childId]
            } else {
                timeline.set(
                    `#${childId}`,
                    {
                        transform: `translateX(${this.lastWidth}px)`,
                    },
                    0
                )
                this.lastWidth += this.newWidths[childId]
            }
        }
        timeline.set(`#${maskObject.id}`, {
            x: `0px`,
            onComplete: () => {
                this.repeat()
            },
        })
    }

    private repeat() {
        const { timeline, maskObject, textsWidth, crawlSpeed } = this
        this.lastWidth = 0
        timeline.clear()
        const tickerObjectElement =
            maskObject.parentId && (document.getElementById(maskObject.parentId) as HTMLElement)
        if (tickerObjectElement) {
            timeline.set(`#${maskObject.id}`, {
                x: `${tickerObjectElement.clientWidth}px`,
            })

            timeline.fromTo(
                `#${maskObject.id}`,
                { transform: `translateX(${tickerObjectElement.clientWidth}px)` },
                {
                    transform: `translateX(-${textsWidth}px)`,
                    ease: "none",
                    duration: crawlSpeed,
                },
                0
            )

            timeline.set(`#${this.maskObject.id}`, {
                x: "0px",
                onComplete: () => this.handleRepeat(() => this.repeat()),
            })
        }
    }

    protected outro(nextTexts?: string) {
        const { timeline, maskObject, textsWidth, crawlSpeed, textListNode } = this

        this.lastWidth = 0
        timeline.clear()
        const tickerObjectElement =
            maskObject.parentId && (document.getElementById(maskObject.parentId) as HTMLElement)
        if (tickerObjectElement) {
            timeline.set(`#${maskObject.id}`, {
                x: `${tickerObjectElement.clientWidth}px`,
            })

            timeline.fromTo(
                `#${maskObject.id}`,
                { transform: `translateX(${tickerObjectElement.clientWidth}px)` },
                {
                    transform: `translateX(-${textsWidth}px)`,
                    ease: "none",
                    duration: crawlSpeed,
                },
                0
            )

            timeline.set(`#${maskObject.id}`, {
                x: `0px`,
                onComplete: () => {
                    timeline.clear()
                    for (const childId of maskObject.childIds) {
                        if (this.lastWidth === 0) {
                            timeline.set(
                                `#${childId}`,
                                {
                                    transform: `translateX(${0}px)`,
                                },
                                0
                            )
                            this.lastWidth = this.newWidths[childId]
                        } else {
                            timeline.set(
                                `#${childId}`,
                                {
                                    transform: `translateX(${this.lastWidth}px)`,
                                },
                                0
                            )
                            this.lastWidth += this.newWidths[childId]
                        }
                    }

                    if (nextTexts) {
                        // prepneme na nové dáta
                        this.currentJoinedTexts = nextTexts
                        // prepočítaj splittedTexts a šírky
                        const measured = applyJoinedCrawlTexts(
                            textListNode,
                            maskObject.childIds,
                            this.currentJoinedTexts
                        )

                        this.newWidths = measured.newWidths
                        this.textsWidth = measured.textsWidth

                        this.crawlSpeed = Number((this.maskWidth + textsWidth) / this.duration)
                    }
                    this.intro()
                },
            })
        }
    }
}
